import React, { Component, PropTypes } from 'react';
import partial from 'lodash/partial';
import pick from 'lodash/pick';
import ModalDialog from 'common/components/modal/modal_dialog';
import PAPEditReviewSection from 'pap/containers/review_editor';
import { PAP_ANSWER_RENDERER_COMPONENTS } from 'pap/components/common/answer_renderers';

/**
 * Generic editable brief section, defined by a title and a list of fields.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class EditableSection extends Component {
  constructor(props) {
    super(props);

    this.editItem = this.editItem.bind(this);
  }

  getItemValue(item, editing) {
    /* get value for a given section */
    const { project, attachments, customContract } = this.props;

    /* file uploads are stored separately, outside of brief answers */
    if (item.name === 'file_upload') {
      return { file_upload: attachments };
    }

    /* ICA contract is stored separately, outside of brief answers */
    if (item.name === 'contract') {
      if (customContract) {
        return { contract: { isCustom: true, custom_contract: customContract } };
      }
      return {
        contract: pick(project, ['client_legal_name', 'client_legal_country']),
        projectId: project.id
      };
    }

    /* defaults */
    if (editing) {
      return pick(project.brief_answers, [item.name]);
    }
    return project.brief_answers[item.name];
  }

  editItem(item) {
    /* use PAP review editor to edit item */
    const nameAndSteps = { name: item.name, steps: [item] };
    const type = item.name === 'contract'? 'details' : 'brief';
    this.props.editPapSection(type, nameAndSteps, this.getItemValue(item, true));
  }

  render() {
    const {
      title, project, items, user, cancelEditPapSection, editingPapSection, canEdit,
    } = this.props;

    /* show modal to edit item if that's the case */
    if (editingPapSection) {
      return (
        <ModalDialog
          modalOpen={true}
          dialogClasses="edit-brief-item-modal"
          closeAction={cancelEditPapSection}
        >
          <PAPEditReviewSection
            sectionData={editingPapSection.data}
            type={editingPapSection.type}
          />
        </ModalDialog>
      );
    }

    return (
      <section className="row brief-section">
        <div className="small-16 large-4 columns brief-title">
          <h2>{ title }</h2>
        </div>

        <div className="small-16 large-8 columns brief-content">
          {items.map((item, i) => {
            const DisplayComponent = PAP_ANSWER_RENDERER_COMPONENTS[item.type];

            if (!DisplayComponent) {
              return null;
            }

            const value = this.getItemValue(item);

            if (!value && !canEdit) {
              return null;
            }

            return (
              <div key={i} className="item review" id={item.name}>
                <h5>{item.answer_label}</h5>

                <DisplayComponent
                  value={value}
                  projectData={project}
                  userData={user}
                  fieldDefinition={item}
                />

                {canEdit && <a className="edit-button" onClick={partial(this.editItem, item)}>
                  <i className="fa fa-pencil" /> Edit
                </a>}
              </div>
            );
          })}
        </div>
      </section>
    );
  }
}

EditableSection.defaultProps = {
  canEdit: false,
};

/**
 * PropTypes.
 *
 * @property {object} project - project
 * @property {object} user - logged in user
 * @property {array} attachments - project attachments
 * @property {object} customContract - project custom ICA contract
 * @property {func} editPapSection - show PAP section review component
 * @property {func} cancelEditPapSection - hide PAP section review component
 * @property {object} editingPapSection - PAP section review info
 * @property {str} title - section title
 * @property {array} items - items to populate this section
 */
EditableSection.propTypes = {
  project: PropTypes.object,
  user: PropTypes.object,
  attachments: PropTypes.array,
  customContract: PropTypes.object,
  editPapSection: PropTypes.func,
  cancelEditPapSection: PropTypes.func,
  editingPapSection: PropTypes.object,
  title: PropTypes.string,
  items: PropTypes.array,
  canEdit: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./project/components/brief/editable_section.jsx